import { forwardRef, useEffect, useRef, useState } from "react";
import { RotateCcw, Trash2, ImageOff } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type PILPhoto = {
  id: string;
  kind: "overview" | "degradation" | "pole_wide" | "pole_side" | "pole_base";
  storage_path: string;
  url?: string;
};

export type AdminPILFormData = {
  id: string;
  ordinal: number;
  lat: number;
  lng: number;
  status: "pending" | "in_progress" | "done";
  is_viable: boolean | null;
  general_comments: string | null;
  admin_notes: string | null;
  photos?: PILPhoto[];
};

interface AdminPILFormProps {
  pil: AdminPILFormData;
  locationId: string;
  isSelected?: boolean;
  onSelect?: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-700 border-gray-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function StatusChip({ status }: { status: string }) {
  return (
    <Badge
      variant="outline"
      className={cn("text-xs font-medium capitalize", STATUS_COLORS[status])}
    >
      {status.replace("_", " ")}
    </Badge>
  );
}

function PhotoThumb({ photo }: { photo: PILPhoto }) {
  const { data } = trpc.physicalMapping.getPhotoSignedUrl.useQuery(
    { photoId: photo.id },
    { staleTime: 5 * 60_000 },
  );
  return (
    <div className="size-16 rounded-md overflow-hidden border bg-muted relative shrink-0">
      {data?.url ? (
        <img
          src={data.url}
          alt={photo.kind}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex items-center justify-center w-full h-full text-muted-foreground">
          <ImageOff className="size-4" />
        </div>
      )}
    </div>
  );
}

const POLE_PHOTO_KINDS = [
  { kind: "pole_wide" as const, label: "Wide view" },
  { kind: "pole_side" as const, label: "Side/profile" },
  { kind: "pole_base" as const, label: "Base/footer" },
];

function PolePhotoRow({ photo, label }: { photo: PILPhoto | undefined; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {photo ? (
        <PhotoThumb photo={photo} />
      ) : (
        <div className="size-16 rounded-md border border-dashed bg-muted/40 flex items-center justify-center shrink-0">
          <ImageOff className="size-4 text-muted-foreground/40" />
        </div>
      )}
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

export const AdminPILForm = forwardRef<HTMLDivElement, AdminPILFormProps>(
  function AdminPILForm({ pil, locationId, isSelected, onSelect }, ref) {
    const utils = trpc.useUtils();

    const [comments, setComments] = useState(pil.general_comments ?? "");
    const [adminNotes, setAdminNotes] = useState(pil.admin_notes ?? "");

    const lastSyncedId = useRef(pil.id);
    useEffect(() => {
      if (lastSyncedId.current !== pil.id) {
        lastSyncedId.current = pil.id;
      }
      setComments(pil.general_comments ?? "");
      setAdminNotes(pil.admin_notes ?? "");
    }, [pil.id, pil.general_comments, pil.admin_notes]);

    const updateMutation = trpc.physicalMapping.updatePIL.useMutation({
      onSuccess: () => {
        utils.physicalMapping.getLocationDetails.invalidate({ locationId });
      },
      onError: (e: { message: string }) => toast.error(`Save failed: ${e.message}`),
    });

    const deleteMutation = trpc.physicalMapping.deletePIL.useMutation({
      onSuccess: () => {
        utils.physicalMapping.getLocationDetails.invalidate({ locationId });
        utils.physicalMapping.list.invalidate();
        toast.success("PIL deleted");
      },
      onError: (e: { message: string }) => toast.error(e.message),
    });

    const resetMutation = trpc.physicalMapping.resetPIL.useMutation({
      onSuccess: () => {
        utils.physicalMapping.getLocationDetails.invalidate({ locationId });
        toast.success("PIL reset to pending");
      },
      onError: (e: { message: string }) => toast.error(e.message),
    });

    const patch = (p: Record<string, unknown>) => {
      updateMutation.mutate({ id: pil.id, patch: p });
    };

    const polePhotosByKind = new Map(
      (pil.photos ?? [])
        .filter((p) => p.kind.startsWith("pole_"))
        .map((p) => [p.kind, p]),
    );

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-lg border p-3 space-y-3 transition-colors",
          isSelected
            ? "ring-2 ring-primary border-primary/40"
            : "bg-card",
        )}
        onClick={onSelect}
      >
        {/* Header */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Installation spot {pil.ordinal}</span>
          <StatusChip status={pil.status} />
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              title="Reset to pending"
              onClick={(e) => {
                e.stopPropagation();
                resetMutation.mutate({ id: pil.id });
              }}
              disabled={resetMutation.isPending}
            >
              <RotateCcw className="size-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive hover:text-destructive"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Delete PIL ${pil.ordinal}?`)) {
                  deleteMutation.mutate({ id: pil.id });
                }
              }}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>

        {/* GPS */}
        <div className="text-xs text-muted-foreground font-mono">
          {pil.lat.toFixed(6)}, {pil.lng.toFixed(6)}
          <span className="ml-2 italic">(drag pin to move)</span>
        </div>

        {/* Pole photos */}
        <div>
          <Label className="text-xs text-muted-foreground">Pole photos</Label>
          <div className="flex gap-3 mt-1">
            {POLE_PHOTO_KINDS.map(({ kind, label }) => (
              <PolePhotoRow
                key={kind}
                photo={polePhotosByKind.get(kind)}
                label={label}
              />
            ))}
          </div>
        </div>

        {/* Is viable */}
        <div className="flex items-center justify-between pt-1">
          <Label className="text-sm">Is viable</Label>
          <Switch
            checked={pil.is_viable === true}
            onCheckedChange={(v) => patch({ is_viable: v })}
          />
        </div>

        {/* Comments */}
        <div>
          <Label className="text-sm">General comments</Label>
          <Textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            onBlur={() => {
              if (comments !== (pil.general_comments ?? "")) {
                patch({ general_comments: comments || null });
              }
            }}
            className="mt-1 text-sm min-h-[60px]"
          />
        </div>

        {/* Admin notes */}
        <div>
          <Label className="text-sm">
            Internal notes
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              not shown to mapper
            </span>
          </Label>
          <Textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            onBlur={() => {
              if (adminNotes !== (pil.admin_notes ?? "")) {
                patch({ admin_notes: adminNotes || null });
              }
            }}
            className="mt-1 text-sm min-h-[60px] bg-amber-50/50 border-amber-200 dark:bg-amber-900/10"
          />
        </div>
      </div>
    );
  },
);
